import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData, SetMultiImageDisplayModeClickBiEvent } from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import ScrollIcon from '@wix/da-ds/pkg/Icons/24x24/Scroll';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { Dims, MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { useMeasureElement } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElement';
import noop from '../../utils/noop';
import { useDeviationViewContext } from '../../Context';
import DuperbrowsePaddles from '../../Duperbrowse/DuperbrowsePaddles';
import StageBackground from '../../DeviationBackground/StageBackground';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import StageContainer from '../_partials/StageContainer';
import AdditionalMediaSlider from '../_partials/AdditionalMediaSlider';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import StashPaddles from '../_partials/StashPaddles';
import FullImageSourceWithCaption from '../_partials/FullImageSourceWithCaption';
import { BI_CAROUSEL_SECTIONNAME } from '../constants';
import { DefaultArtStageProps as Props } from '../types';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';

import styles from './DesktopArtStage.scss';

/** Multi-image deviation that displays as a slider of thumbnails allowing the user
 *  to choose what image is displayed in a larger single image above.
 *
 *  Also currently the only multi-image stage that correctly shows mature, paywall
 *  blocking. */
export const DesktopMultiArtCarouselStage: React.FC<Props> = ({
  deviation,
  additionalMedia,
  zoomedImageSource,
  isZoomed,
  disabled,
  shouldBlur,
  currentlyViewedImageSource,
  setCurrentlyViewedImageSource = noop,
  className,
  onEnterZoomMode,
  onExitZoomMode,
  setDisplayMode = noop,
}) => {
  const { t } = useTranslation();
  const isDuperbrowseActive = useSelector(getIsDuperbrowseActive);

  const { isFullscreen } = useDeviationViewContext();
  const { ref: contentRef, dimensions: contentSize = {} as Dims } = useMeasureElementCookie(
    isFullscreen ? undefined : MeasuredCookieType.DEVIATION_MULTI_IMAGE_CAROUSEL,
    { cookieDim: 'both' }
  );
  const { ref: containerRef, dimensions: containerSize } = useMeasureElement();

  const isSubscriptionPurchaseFTUEBeingShown = !!useContext(SubscriptionPurchaseFTUEContext);

  return (
    <StageContainer
      className={classnames(
        className,
        styles['root'],
        styles['carousel-mode'],
        isFullscreen && styles['fullscreen']
      )}
      withoutInnerWrapper
      ref={containerRef}
    >
      <BiLoggerContextProvider value={BI_CAROUSEL_SECTIONNAME}>
        <div className={classnames(styles['content'])} ref={contentRef}>
          <StageBackground
            deviation={deviation}
            width={containerSize?.width}
            height={containerSize?.height}
          />

          <FullImageSourceWithCaption
            deviationOrAttachment={
              currentlyViewedImageSource ? currentlyViewedImageSource : deviation
            }
            maintainHeightForCaptions
            {...contentSize}
            withoutZoomModal
            isZoomed={isZoomed}
            onEnterZoomMode={onEnterZoomMode}
            onExitZoomMode={onExitZoomMode}
          />

          {(deviation.isPublished || isDuperbrowseActive) && (
            <DuperbrowsePaddles className={styles['duperbrowse']} isInZoomModal={isZoomed} />
          )}
          {!deviation.isPublished && !isDuperbrowseActive && <StashPaddles />}
        </div>

        <Section className={styles['carousel-controls']}>
          <VisuallyHidden>
            <Heading>{t('deviationview.artstage.multi.carouselControls.a11yHeading')}</Heading>
          </VisuallyHidden>

          <AdditionalMediaSlider
            className={styles['additional-media-slider']}
            deviation={deviation}
            additionalMedia={additionalMedia}
            disabled={disabled}
            shouldBlur={shouldBlur}
            selected={currentlyViewedImageSource}
            onSelect={attachment => setCurrentlyViewedImageSource(attachment)}
          />
          <IconButton
            className={styles['switch-to-scroll-button']}
            icon={ScrollIcon}
            size="large"
            tooltipText={t('deviationview.artstage.changeToScrollMode.tooltip')}
            tooltipOffset={[0, 14]}
            aria-label={t('deviationview.artstage.changeToScrollMode.altLabel')}
            disabled={disabled}
            onClick={() => setDisplayMode('scroll')}
            biData={BiData<SetMultiImageDisplayModeClickBiEvent>({
              evid: 309,
              deviationid: deviation.deviationId,
              view_mode: 'scroll',
            })}
          />
        </Section>

        {zoomedImageSource && (
          <ZoomModalFromImageSource
            source={zoomedImageSource}
            allowFullviewSrcsetForPngs={(zoomedImageSource as PapiDeviation)?.isPublished ?? false}
            {...contentSize}
            isZoomed={isZoomed}
            onExitZoomMode={onExitZoomMode}
          />
        )}

        {isSubscriptionPurchaseFTUEBeingShown && (
          <SubscriptionPurchaseFTUE className={styles['subscription-purchase-ftue']} />
        )}
      </BiLoggerContextProvider>
    </StageContainer>
  );
};
DesktopMultiArtCarouselStage.displayName = 'DesktopMultiArtCarouselStage';
export default DesktopMultiArtCarouselStage;
